import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp,
  Users,
  Star,
  BarChart,
  FileText,
  Calendar,
  ArrowUp,
  ArrowDown,
  Loader2,
} from 'lucide-react';

interface DashboardStats {
  profileViews: number;
  connections: number;
  contentEngagement: number;
  profileScore: number;
  weeklyGrowth: {
    views: { current: number; previous: number };
    connections: { current: number; previous: number };
  };
}

interface RecentActivity {
  id: string;
  type: 'profile_update' | 'connection' | 'interview';
  title: string;
  description: string;
  timestamp: string;
}

export function ProDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    profileViews: 0,
    connections: 0,
    contentEngagement: 0,
    profileScore: 0,
    weeklyGrowth: {
      views: { current: 0, previous: 0 },
      connections: { current: 0, previous: 0 }
    }
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);

  useEffect(() => {
    if (user) {
      fetchDashboardStats();
    }
  }, [user]);

  const fetchDashboardStats = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Fetch profile views
      const { count: profileViewsCount, error: viewsError } = await supabase
        .from('profile_views')
        .select('*', { count: 'exact', head: true })
        .eq('profile_id', user.id);
        
      if (viewsError) throw viewsError;
      
      // Fetch connections
      const { count: connectionsCount, error: connectionsError } = await supabase
        .from('profile_connections')
        .select('*', { count: 'exact', head: true })
        .or(`requester_id.eq.${user.id},receiver_id.eq.${user.id}`)
        .eq('status', 'accepted');
        
      if (connectionsError) throw connectionsError;
      
      // Calculate weekly growth stats
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
      
      // Views growth
      const { count: currentWeekViewsCount, error: currentViewsError } = await supabase
        .from('profile_views')
        .select('*', { count: 'exact', head: true })
        .eq('profile_id', user.id)
        .gte('created_at', oneWeekAgo.toISOString());
        
      if (currentViewsError) throw currentViewsError;
      
      const { count: previousWeekViewsCount, error: previousViewsError } = await supabase
        .from('profile_views')
        .select('*', { count: 'exact', head: true })
        .eq('profile_id', user.id)
        .gte('created_at', twoWeeksAgo.toISOString())
        .lt('created_at', oneWeekAgo.toISOString());
        
      if (previousViewsError) throw previousViewsError;
      
      // Connections growth
      const { count: currentWeekConnectionsCount, error: currentConnectionsError } = await supabase
        .from('profile_connections')
        .select('*', { count: 'exact', head: true })
        .or(`requester_id.eq.${user.id},receiver_id.eq.${user.id}`)
        .eq('status', 'accepted')
        .gte('created_at', oneWeekAgo.toISOString());
        
      if (currentConnectionsError) throw currentConnectionsError;
      
      const { count: previousWeekConnectionsCount, error: previousConnectionsError } = await supabase
        .from('profile_connections')
        .select('*', { count: 'exact', head: true })
        .or(`requester_id.eq.${user.id},receiver_id.eq.${user.id}`)
        .eq('status', 'accepted')
        .gte('created_at', twoWeeksAgo.toISOString())
        .lt('created_at', oneWeekAgo.toISOString());
        
      if (previousConnectionsError) throw previousConnectionsError;
      
      // Fetch recent activity
      const [profileUpdates, connections, interviews] = await Promise.all([
        // Profile updates
        supabase
          .from('profiles')
          .select('updated_at')
          .eq('id', user.id)
          .single(),
        // Recent connections
        supabase
          .from('profile_connections')
          .select(`
            id,
            status,
            created_at,
            profiles!profile_connections_receiver_id_fkey(
              full_name
            )
          `)
          .or(`requester_id.eq.${user.id},receiver_id.eq.${user.id}`)
          .order('created_at', { ascending: false })
          .limit(5),
        // Recent interviews
        supabase
          .from('interviews')
          .select(`
            id,
            title,
            date,
            time,
            status,
            recruiting_projects(title)
          `)
          .or(`participants.cs.{${user.email}},profile_id.eq.${user.id}`)
          .order('date', { ascending: true })
          .limit(5)
      ]);
      
      // Calculate profile score based on completeness
      const { data: profileData } = await supabase
        .from('profiles')
        .select(`
          *,
          experiences (id),
          education (id),
          projects (id)
        `)
        .eq('id', user.id)
        .single();
      
      let profileScore = 0;
      if (profileData) {
        if (profileData.full_name) profileScore += 10;
        if (profileData.title) profileScore += 10;
        if (profileData.summary) profileScore += 10;
        if (profileData.location) profileScore += 5;
        if (profileData.experiences?.length > 0) profileScore += 25;
        if (profileData.education?.length > 0) profileScore += 20;
        if (profileData.projects?.length > 0) profileScore += 20;
      }
      
      // Update state with real data
      setStats({
        profileViews: profileViewsCount || 0,
        connections: connectionsCount || 0,
        contentEngagement: 0, // Since content_interactions doesn't exist, default to 0
        profileScore,
        weeklyGrowth: {
          views: {
            current: currentWeekViewsCount || 0,
            previous: previousWeekViewsCount || 0
          },
          connections: {
            current: currentWeekConnectionsCount || 0,
            previous: previousWeekConnectionsCount || 0
          }
        }
      });
      
      // Transform activity data into unified format
      const activities: RecentActivity[] = [];
      
      // Add profile updates
      if (profileUpdates.data) {
        activities.push({
          id: 'profile-update-' + Date.now(),
          type: 'profile_update',
          title: 'Profile Updated',
          description: 'You updated your profile information',
          timestamp: profileUpdates.data.updated_at
        });
      }
      
      // Add connections
      if (connections.data) {
        connections.data.forEach((connection: any) => {
          activities.push({
            id: connection.id,
            type: 'connection',
            title: 'Connection ' + connection.status,
            description: connection.profiles?.full_name 
              ? `Connection with ${connection.profiles.full_name}` 
              : 'New connection',
            timestamp: connection.created_at
          });
        });
      }
      
      // Add interviews
      if (interviews.data) {
        interviews.data.forEach((interview: any) => {
          activities.push({
            id: interview.id,
            type: 'interview',
            title: interview.title,
            description: interview.recruiting_projects?.title 
              ? `Interview for ${interview.recruiting_projects.title}` 
              : 'Upcoming interview',
            timestamp: interview.date
          });
        });
      }
      
      // Sort by timestamp
      activities.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      
      setRecentActivity(activities);
      
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load dashboard data',
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateGrowthPercentage = (current: number, previous: number) => {
    if (previous === 0) return 100;
    return Math.round(((current - previous) / previous) * 100);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Pro Dashboard</h1>
        <p className="text-muted-foreground">
          Track your professional growth and engagement
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Profile Views</p>
                <h3 className="text-2xl font-bold mt-2">{stats.profileViews}</h3>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <Badge variant={calculateGrowthPercentage(stats.weeklyGrowth.views.current, stats.weeklyGrowth.views.previous) >= 0 ? "default" : "destructive"} className="gap-1">
                {calculateGrowthPercentage(stats.weeklyGrowth.views.current, stats.weeklyGrowth.views.previous) >= 0 ? (
                  <ArrowUp className="h-3 w-3" />
                ) : (
                  <ArrowDown className="h-3 w-3" />
                )}
                {Math.abs(calculateGrowthPercentage(stats.weeklyGrowth.views.current, stats.weeklyGrowth.views.previous))}%
              </Badge>
              <span className="ml-2 text-muted-foreground">vs last week</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Profile Score</p>
                <h3 className="text-2xl font-bold mt-2">{stats.profileScore}</h3>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Star className="h-6 w-6 text-primary" />
              </div>
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              Profile completeness score
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Connections</p>
                <h3 className="text-2xl font-bold mt-2">{stats.connections}</h3>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <Badge variant={calculateGrowthPercentage(stats.weeklyGrowth.connections.current, stats.weeklyGrowth.connections.previous) >= 0 ? "default" : "destructive"} className="gap-1">
                {calculateGrowthPercentage(stats.weeklyGrowth.connections.current, stats.weeklyGrowth.connections.previous) >= 0 ? (
                  <ArrowUp className="h-3 w-3" />
                ) : (
                  <ArrowDown className="h-3 w-3" />
                )}
                {Math.abs(calculateGrowthPercentage(stats.weeklyGrowth.connections.current, stats.weeklyGrowth.connections.previous))}%
              </Badge>
              <span className="ml-2 text-muted-foreground">vs last week</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Engagement</p>
                <h3 className="text-2xl font-bold mt-2">{stats.contentEngagement}</h3>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                <BarChart className="h-6 w-6 text-primary" />
              </div>
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              Content engagement score
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Your latest professional activities</CardDescription>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-muted-foreground">No recent activity</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      {activity.type === 'profile_update' && <FileText className="h-5 w-5 text-primary" />}
                      {activity.type === 'connection' && <Users className="h-5 w-5 text-primary" />}
                      {activity.type === 'interview' && <Calendar className="h-5 w-5 text-primary" />}
                    </div>
                    <div>
                      <p className="font-medium">{activity.title}</p>
                      <p className="text-sm text-muted-foreground">{activity.description}</p>
                    </div>
                    <Badge variant="outline" className="ml-auto">{activity.timestamp}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Profile Strength</CardTitle>
            <CardDescription>Areas to improve your profile</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-muted-foreground">Skills</span>
                  <span className="font-medium">90%</span>
                </div>
                <Progress 
                  value={90} 
                  className="h-2" 
                />
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-muted-foreground">Experience</span>
                  <span className="font-medium">85%</span>
                </div>
                <Progress 
                  value={85} 
                  className="h-2" 
                />
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-muted-foreground">Projects</span>
                  <span className="font-medium">70%</span>
                </div>
                <Progress 
                  value={70} 
                  className="h-2" 
                />
              </div>
              <Button variant="outline" className="w-full mt-4" asChild>
                <Link to={`/profile/${user?.id}`}>
                  Complete Your Profile
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}